/**
 * PanelHeader — the icon + title + description block every demo panel opens
 * with.
 *
 * Demos repeatedly hand-roll the same `<header><h2>…</h2><p>…</p></header>`
 * structure (attack-surface-convergence extracted a local `PanelHeader`; a
 * dozen other panels inline it). This is the shared, theme-agnostic home for
 * that structure: it owns the markup, the decorative-icon a11y treatment, and
 * the `React.memo` wrapper, while leaving all color/typography to the caller
 * via `titleStyle` / `subtitleStyle` (and optional class passthroughs). A demo
 * adopts it without changing how its header looks — it just stops re-spelling
 * the structure.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { cn } from "../styling/index.js";

const DEFAULT_TITLE_STYLE: React.CSSProperties = {
  fontSize: "1.25rem",
  fontWeight: 700,
  marginBottom: "0.25rem",
};

const DEFAULT_DESCRIPTION_STYLE: React.CSSProperties = {
  color: "var(--csk-panel-muted, #6b7a8d)",
  fontSize: "0.75rem",
  marginBottom: "1rem",
};

export interface PanelHeaderProps {
  /** Optional leading glyph/node, rendered decoratively (`aria-hidden`). */
  readonly icon?: React.ReactNode;
  /** Panel title (the `h2`). */
  readonly title: string;
  /** Optional sub-title / description paragraph. Omitted when absent. */
  readonly description?: React.ReactNode;
  /** Inline style for the title `h2` — usually a demo's panel-title token. */
  readonly titleStyle?: React.CSSProperties;
  /** Inline style for the description `p` — usually a demo's subtitle token. */
  readonly subtitleStyle?: React.CSSProperties;
  /** Classes for the `header` wrapper. */
  readonly className?: string;
  /** Classes for the title `h2`. */
  readonly titleClassName?: string;
  /** Classes for the description `p`. */
  readonly descriptionClassName?: string;
}

function PanelHeaderImpl({
  icon,
  title,
  description,
  titleStyle,
  subtitleStyle,
  className,
  titleClassName,
  descriptionClassName,
}: PanelHeaderProps): React.JSX.Element {
  return (
    <header className={className}>
      <h2 className={titleClassName} style={titleStyle ?? DEFAULT_TITLE_STYLE}>
        {icon !== undefined ? (
          <span aria-hidden="true" className={cn("mr-[0.4em]")}>
            {icon}
          </span>
        ) : null}
        {title}
      </h2>
      {description !== undefined ? (
        <p className={descriptionClassName} style={subtitleStyle ?? DEFAULT_DESCRIPTION_STYLE}>
          {description}
        </p>
      ) : null}
    </header>
  );
}

export const PanelHeader = React.memo(PanelHeaderImpl);
